<script setup lang="ts">
import {
	mdiArrowLeft,
	mdiArrowRight,
	mdiArrowULeftTop,
	mdiCheck,
	mdiCheckboxMarkedCirclePlusOutline,
	mdiClose,
	mdiUnfoldMoreHorizontal,
} from "@mdi/js";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { betwayBetbookCashout, betwayBoostedOdds } from "~/assets/betwayIcons";
import { useBetslip } from "~/store/betslip";
import { useAuth } from "~/store/auth";
import { useSettings } from "~/store/settings";
import type { IEvent } from "~/types/sports/event";
import type { IMarket } from "~/types/sports/market";
import type { IOutcome } from "~/types/sports/outcome";
import type { IPrice } from "~/types/sports/price";
import type { IFilterMarket, ISport } from "~/types/sports/sport";
import type { ISwiperContent, ISwiperData } from "~/types/swipe-bet/content";
import type { ISelection, IfilterData } from "~/types/swipe-bet/filter";
import api from "~/utils/auth/api";
import formatOdds from "~/utils/formatOdds";
import t from "~/utils/translate";
import NavIcon from "../../layout/Nav/NavIcon.vue";
import SwipeBetHeader from "./SwipeBetHeader.vue";
import SwipeBetNoMoreSwipes from "./SwipeBetNoMoreSwipes.vue";
import SwipeBetTutorial from "./SwipeBetTutorial.vue";
import Swiper from "./Swiper.vue";
import { getJerserySrc, handleTeamJerseyError, hasLeagueJersey } from "~/utils/getJerseySrc";

interface SwipePointer {
	eventId: string;
	marketIds: string[] | null;
	swipeBetId: string;
	sportId: string;
	selectedEventId: string;
	selectedMarketId: string;
	selectedOutcomeId: string;
	eventDate: string;
	decision?: "like" | "nope" | "super" | "down";
}

interface ISwiperContentWithDecision extends ISwiperContent {
	decision?: "like" | "nope" | "super" | "down";
	actualOutcomeId?: string;
}

interface ISwiperRef {
	decide: (choice: string) => void;
	rewind: () => boolean;
}

interface IEmop {
	event: IEvent;
	markets: IMarket[];
	outcomes: IOutcome[];
	prices: IPrice[];
}

const config = useRuntimeConfig();
const { $toast } = useNuxtApp();
const auth = useAuth();
const swipeBets = ref<ISwiperContent[]>([]);
const loadedSwipeBets = ref<ISwiperContent[]>([]);
const swipePointers = ref<SwipePointer[]>([]);

const betslip = useBetslip();
const settings = useSettings();
const swiper = ref<ISwiperRef | null>(null);

const offset = ref(0);
const swipedCount = ref(0);
const swipeBetsTotal = ref(0);
const currentBatchCount = ref(0);
const preSelections = ref<ISelection[]>([]);
const sportsFilterMap = ref(new Map<string, IFilterMarket[]>());
const uniqueSportsIDs = ref<string[]>([]);
const eventIds = ref<string[]>([]);
const marketNames = ref<string[]>([]);
const loading = ref(true);
const isRefetching = ref(false);
const noMoreSwipes = ref(false);
const grabbing = ref(false);
const shouldFetch = ref(false);
const swipeCard = ref<HTMLElement | null>();
const clickStartTime = ref(0);
const clickSide = ref("");

const touchStartX = ref(0);
const touchStartY = ref(0);
const swipeThreshold = 10; //pixels

const events = ref(new Map());
const markets = ref(new Map());
const outcomes = ref(new Map());
const prices = ref(new Map());

const activeMarketIndexes = ref<Map<string, number>>(new Map());
const swipedItems = ref<ISwiperContentWithDecision[]>([]);

const reactiveOffset = computed(() => {
	return offset.value;
});

const reactiveAuth = computed(() => {
	return auth.loggedIn;
});

interface ICronSports {
	sports: ISport[];
}

const buttonsDisabled = computed(() => {
	return swipePointers.value.filter((p: SwipePointer) => p.eventId !== "start").length === 0 || isRefetching.value;
});

const undoDisabled = computed(() => {
	return buttonsDisabled.value || swipedItems.value.length === 0;
});

const changeMarketDisabled = computed(() => {
	if (buttonsDisabled.value) return true;

	if (getCurrentEventId() === "start") return true;
	const currentPointer = swipePointers.value.find((pointer) => pointer.eventId !== "start");
	return !currentPointer?.marketIds || currentPointer.marketIds.length <= 1;
});

// Wrapper functions to prevent actions when disabled
const handleUndoClick = () => {
	if (undoDisabled.value) return;
	handleUndo();
};

const handlePassClick = () => {
	if (buttonsDisabled.value) return;
	decide("nope", getCurrentEventId());
};

const handleAddClick = () => {
	if (buttonsDisabled.value) return;
	decide("like", getCurrentEventId());
};

const handleChangeMarketClick = () => {
	if (changeMarketDisabled.value) return;
	handleChangeMarket();
};

const handleNotFoundImages = (e: Event) => {
	const target = e.target as HTMLImageElement;
	if (target) {
		target.src = getRegionFlag("default");
	}
};

const getRegionFlag = (region: string) => {
	return `https://cms1.betwayafrica.com/medialibraries/content.gmgamingsystems.com/Synapse/icons/regions-leagues-flags/${region}-circular.svg`;
};

const reactiveShouldFetch = computed(() => {
	if (sportsFilterMap.value.size === 0) {
		return false;
	}

	if (auth.hydrated && auth.loggedIn) {
		return shouldFetch.value;
	}

	if (auth.hydrated && !auth.loggedIn) {
		return true;
	}

	return false;
});

useQuery({
	queryKey: ["swipe-bet-player-meta"],
	queryFn: () =>
		api({
			url: "/Widget/SwipeBets/PlayerMetaData",
			type: "api",
			options: {
				method: "GET",
			},
		}),
	enabled: reactiveAuth,
	onSuccess: (data: IfilterData[]) => {
		const playerMetaData = data;
		for (const data of playerMetaData) {
			for (const region of data.regions) {
				for (const league of region.leagues) {
					const alreadyExists = preSelections.value.some(
						(item) => item.sport === data.name && item.leagueId === league.name && item.regionId === region.name
					);

					if (!alreadyExists) {
						preSelections.value = [...(preSelections.value || []), { sport: data.name, leagueId: league.name, regionId: region.name }];
					}
				}
			}
		}
		shouldFetch.value = true;
	},
});

useQuery({
	queryKey: ["sports"],
	queryFn: async () => {
		try {
			const result = await api({
				url: "/cron/sports/{brand}/{locale}",
				type: "config",
				options: {
					method: "GET",
				},
			});
			return result;
		} catch (error) {
			return { sports: [] };
		}
	},
	onSuccess: (data: ICronSports) => {
		if (data?.sports) {
			for (const sport of data.sports) {
				const filterMarkets = sport.filterMarkets || [];
				sportsFilterMap.value.set(sport.sportId, filterMarkets);
			}
		}
	},
});

useQuery({
	queryKey: ["swipe-bet", reactiveOffset],
	queryFn: () =>
		api({
			url: `/Widget/SwipeBets?skip=${reactiveOffset.value}&limit=10&source={source}${preSelections.value
				.map((sel) => `&sport=${sel.sport}&regionId=${sel.regionId}`)
				.join("")}`,
			type: "api",
			options: {
				method: "GET",
				headers: {
					"X-Region-Code": settings.config?.brandSuffix as string,
				},
			},
		}),
	enabled: reactiveShouldFetch,
	onSuccess: (data: ISwiperData) => {
		if (!data.data.length) {
			loading.value = false;
			noMoreSwipes.value = true;
		} else {
			noMoreSwipes.value = false;
			if (offset.value === 0) {
				// Fresh load - replace all data
				swipeBets.value = [...data.data];
			} else {
				// Pagination - add to existing data
				swipeBets.value = [...swipeBets.value, ...data.data];
			}
			currentBatchCount.value = data.data.length;

			eventIds.value = data.data.map((item: ISwiperContent) => item.selectedEventId);
			uniqueSportsIDs.value = [...new Set(swipeBets.value.map((item: ISwiperContent) => item.sportId))];

			// Clear previous market names and rebuild for the current sports
			marketNames.value = [];
			for (const sportId of uniqueSportsIDs.value) {
				const sportMarkets = sportsFilterMap.value.get(sportId);
				if (sportMarkets && sportMarkets.length > 0) {
					// Add all market names for this sport (avoiding duplicates)
					for (const market of sportMarkets) {
						if (!marketNames.value.includes(market.name)) {
							marketNames.value.push(market.name);
						}
					}
				}
			}
			currentBatchCount.value = data.data.length;

			if (!eventIds.value.length) {
				offset.value += currentBatchCount.value;
			} else {
				mutate();
			}
		}
	},
});

const refetchEmop = (data: ISwiperContent[]) => {
	isRefetching.value = true; // This is a filter refetch, show loading
	loading.value = false; // Don't use the pagination loading state
	noMoreSwipes.value = false; // Reset this as we're starting fresh with new filters
	swipeBetsTotal.value = 0;
	swipedCount.value = 0;

	// Update swipeBets with the new data
	swipeBets.value = [...data];

	// Extract event IDs from the new data
	eventIds.value = data.map((item: ISwiperContent) => item.selectedEventId);

	// Get unique sport IDs from the NEW data
	uniqueSportsIDs.value = [...new Set(data.map((item: ISwiperContent) => item.sportId))];

	// Clear previous market names and rebuild for the new sports
	marketNames.value = [];
	for (const sportId of uniqueSportsIDs.value) {
		const sportMarkets = sportsFilterMap.value.get(sportId);
		if (sportMarkets && sportMarkets.length > 0) {
			// Add all market names for this sport without duplicates
			for (const market of sportMarkets) {
				if (!marketNames.value.includes(market.name)) {
					marketNames.value.push(market.name);
				}
			}
		}
	}

	currentBatchCount.value = data.length;
	swipePointers.value = [];

	if (!eventIds.value.length) {
		offset.value += currentBatchCount.value;
	} else {
		mutate();
	}
};

const { mutate, isLoading: emopLoading } = useMutation({
	mutationFn: (): Promise<IEmop[]> => {
		if (!eventIds.value.length) {
			return Promise.reject(new Error("No event IDs"));
		}

		return api<IEmop[]>({
			url: `/Feeds/EMOP?${(() => {
				let params = "";
				for (let index = 0; index < eventIds.value.length; index++) {
					params += `&eventIds=${String(eventIds.value[index])}`;
				}
				for (let index = 0; index < marketNames.value.length; index++) {
					params += `&marketNames=${marketNames.value[index]}`;
				}
				return params;
			})()}&CountryCode=${settings.getCountryConfig?.countryIsoTwo}&CultureCode=${settings.locale}&isTransformationRequired=false`,
			type: "sports",
			options: {
				method: "GET",
				headers: {},
			},
		});
	},
	onError: (error) => {
		loading.value = false;
		isRefetching.value = false;
		noMoreSwipes.value = true;
	},
	onSuccess: (data: IEmop[]) => {
		// Only reset maps if this is a fresh load (offset = 0)
		if (offset.value === 0) {
			events.value.clear();
			markets.value.clear();
			outcomes.value.clear();
			prices.value.clear();
			swipePointers.value = [];
		}

		const emopLength = data.length;
		if (!emopLength) {
			loading.value = false;
			isRefetching.value = false;
			noMoreSwipes.value = true;
			return;
		}

		// Check if any EMOP data has betting information (markets, outcomes, prices)
		const validEmopData = data.filter(
			(emop) => emop.markets && emop.markets.length > 0 && emop.outcomes && emop.outcomes.length > 0 && emop.prices && emop.prices.length > 0
		);

		if (validEmopData.length === 0) {
			loading.value = false;
			isRefetching.value = false;
			noMoreSwipes.value = true;
			return;
		}

		noMoreSwipes.value = false;

		// Add start item first only on fresh load
		if (offset.value === 0 && swipePointers.value.length === 0) {
			swipePointers.value.push({
				eventId: "start",
				marketIds: null,
				swipeBetId: "start",
				sportId: "",
				selectedEventId: "start",
				selectedMarketId: "",
				selectedOutcomeId: "",
				eventDate: new Date().toString(),
			});
		}

		// Process each EMOP data item (only the ones with valid betting data)
		const processedEventIds = new Set<string>(swipePointers.value.map((p) => p.eventId).filter((id) => id !== "start"));

		for (const emop of validEmopData) {
			if (!emop.event || !emop.event.eventId) {
				continue;
			}

			const eventId = emop.event.eventId;
			const eventIdString = String(eventId);

			// Check if this event exists in swipeBets before processing
			const correspondingSwipeBet = swipeBets.value.find((bet) => String(bet.selectedEventId) === eventIdString);
			if (!correspondingSwipeBet) {
				continue;
			}

			// Store the event data
			events.value.set(eventId, emop.event);

			// Store market data and collect market IDs
			const marketIdArray: string[] = [];
			if (emop.markets && emop.markets.length > 0) {
				for (const market of emop.markets) {
					markets.value.set(market.marketId, market);
					marketIdArray.push(market.marketId);
				}
			}

			// Store outcome data
			if (emop.outcomes && emop.outcomes.length > 0) {
				for (const outcome of emop.outcomes) {
					outcomes.value.set(outcome.outcomeId, outcome);
				}
			}

			// Store price data
			if (emop.prices && emop.prices.length > 0) {
				for (const price of emop.prices) {
					prices.value.set(price.outcomeId, price);
				}
			}

			// Validate that this event has usable betting data and filter out invalid markets
			const validMarketIds: string[] = [];

			if (marketIdArray.length > 0) {
				// Check each market to see if it has outcomes with prices
				for (const marketId of marketIdArray) {
					// Try both regular and underscore-prefixed market IDs
					const marketIds = [marketId, `_${marketId}`];
					let marketHasValidData = false;

					for (const checkMarketId of marketIds) {
						const marketOutcomes = emop.outcomes?.filter((o) => String(o.marketId) === String(checkMarketId)) || [];

						if (marketOutcomes.length > 0) {
							// Check if at least one outcome has a price
							const hasPrice = marketOutcomes.some((outcome) => emop.prices?.some((price) => String(price.outcomeId) === String(outcome.outcomeId)));

							if (hasPrice) {
								marketHasValidData = true;
								break;
							}
						}
					}

					// Only include markets that have valid betting data
					if (marketHasValidData) {
						validMarketIds.push(marketId);
					}
				}
			}

			// Only add new swipe pointers for events that have valid betting data
			if (!processedEventIds.has(eventIdString) && validMarketIds.length > 0) {
				processedEventIds.add(eventIdString);

				const newPointer: SwipePointer = {
					eventId: eventIdString,
					marketIds: validMarketIds,
					swipeBetId: correspondingSwipeBet.swipeBetId,
					sportId: correspondingSwipeBet.sportId,
					selectedEventId: correspondingSwipeBet.selectedEventId,
					selectedMarketId: correspondingSwipeBet.selectedMarketId,
					selectedOutcomeId: correspondingSwipeBet.selectedOutcomeId,
					eventDate: correspondingSwipeBet.eventDate,
				};

				swipePointers.value.push(newPointer);
				swipeBetsTotal.value++;
			}
		}

		// Check if we have any usable cards after processing
		const usableCards = swipePointers.value.filter((p) => p.eventId !== "start").length;

		// If we have no usable cards and this was a fresh load (offset = 0), try to fetch more
		// But don't do automatic pagination for subsequent loads to avoid flickering
		if (usableCards === 0 && validEmopData.length > 0 && offset.value === 0 && !isRefetching.value) {
			offset.value += currentBatchCount.value;
			noMoreSwipes.value = false; // Reset NoMoreSwipe since we're trying again
			return;
		}

		if (usableCards === 0 && validEmopData.length === 0) {
			noMoreSwipes.value = true;
		} else if (usableCards > 0) {
			// Ensure noMoreSwipes is false when we have cards
			noMoreSwipes.value = false;
		}

		loading.value = false;
		isRefetching.value = false;
	},
});

const onSubmit = ({ type, item }: { type: "like" | "nope" | "super" | "down"; item: SwipePointer }) => {
	if (!item || item.eventId === "start") {
		return;
	}

	const eventId = item.eventId;
	const event = events.value.get(Number(eventId));

	if (!event) {
		return;
	}

	// Find the matching swipe bet first (needed for all swipe types)
	const swipeBet = swipeBets.value.find((bet) => String(bet.selectedEventId) === String(eventId));

	if (!swipeBet) {
		return;
	}
	let actualOutcomeId = swipeBet.selectedOutcomeId;
	if (type === "like") {
		const marketId = getActiveMarketId(eventId, item.marketIds);
		if (marketId) {
			const allOutcomes = Array.from(outcomes.value.values());
			const marketOutcomes = allOutcomes.filter((o) => String(o.marketId) === String(marketId));
			let currentOutcome = marketOutcomes[0];

			// Check for underscore prefix if no outcome found
			if (!currentOutcome) {
				const prefixedMarketId = `_${marketId}`;
				const prefixedOutcomes = allOutcomes.filter((o) => String(o.marketId) === prefixedMarketId);
				if (prefixedOutcomes.length > 0) {
					currentOutcome = prefixedOutcomes[0];
				}
			}

			if (currentOutcome) {
				actualOutcomeId = currentOutcome.outcomeId;
			}
		}
	}

	// Store the swiped item for undo functionality (only for non-super swipes)
	const swipedItem: ISwiperContentWithDecision = {
		...swipeBet,
		decision: type,
		actualOutcomeId: actualOutcomeId,
	};
	swipedItems.value.unshift(swipedItem);

	// Remove the swiped item from both arrays
	const swipeBetIndex = swipeBets.value.findIndex((bet) => String(bet.selectedEventId) === String(eventId));
	if (swipeBetIndex !== -1) {
		swipeBets.value.splice(swipeBetIndex, 1);
	}

	const loadedSwipeBetIndex = loadedSwipeBets.value.findIndex((bet) => String(bet.selectedEventId) === String(eventId));
	if (loadedSwipeBetIndex !== -1) {
		loadedSwipeBets.value.splice(loadedSwipeBetIndex, 1);
	}

	swipedCount.value++;

	if (type === "like") {
		// Check if we can add more selections
		if (
			(betslip.activeTab === "multi" && betslip.selection.length === Number(config.public.maximumBetsStrikeMulti)) ||
			(betslip.activeTab === "single" && betslip.selection.length === Number(config.public.maximumBetsStrikeSingles))
		) {
			$toast.add({
				severity: "error",
				summary: t("error.title"),
				detail: t("multibet-threshold-reached"),
				life: 3000,
			});
		} else {
			// Add to betslip - use the currently displayed outcome, not the original selectedOutcomeId
			const marketId = getActiveMarketId(eventId, item.marketIds);

			if (!marketId) {
				return;
			}

			// Get the currently displayed outcome (same logic as in template)
			const allOutcomes = Array.from(outcomes.value.values());
			const marketOutcomes = allOutcomes.filter((o) => String(o.marketId) === String(marketId));
			let currentOutcome = marketOutcomes[0];

			if (!currentOutcome) {
				// Try to find outcome with underscore prefix
				const prefixedMarketId = `_${marketId}`;
				const prefixedOutcomes = allOutcomes.filter((o) => String(o.marketId) === prefixedMarketId);
				if (prefixedOutcomes.length > 0) {
					// Use the prefixed outcome instead
					currentOutcome = prefixedOutcomes[0];
				} else {
					return;
				}
			}

			const outcomeId = String(currentOutcome.outcomeId);

			// Check if this bet already exists in the betslip
			const existingBet = betslip.selection.find((item) => String(item.outcomeId) === outcomeId && String(item.eventId) === String(event.eventId));

			if (existingBet && betslip.activeTab === "multi") {
				$toast.add({
					severity: "info",
					detail: t("bet-already-added"),
					life: 3000,
				});
				return;
			}

			const market = markets.value.get(marketId);
			const price = prices.value.get(outcomeId);

			if (market && currentOutcome && price && !(existingBet && betslip.activeTab === "single")) {
				betslip.handleSelection({
					event: event,
					market: market,
					outcome: currentOutcome,
					price: price,
					score: null,
					source: "swipe-bet",
				});
			}
		}
	}

	const remainingCards = swipePointers.value.filter((p) => p.eventId !== "start").length;

	if (remainingCards <= 3 && loadedSwipeBets.value.length <= 3 && !loading.value && !noMoreSwipes.value) {
		noMoreSwipes.value = false;
		offset.value += currentBatchCount.value;
	}
};
const decide = (choice: string, swipeBetId: string) => {
	if (
		choice === "like" &&
		betslip.activeTab === "single" &&
		betslip.selection.length === Number(config.public.maximumBetsStrikeSingles) &&
		swipeBetId !== "start"
	) {
		$toast.add({
			severity: "error",
			summary: t("error.title"),
			detail: t("betslip-threshold-reached"),
			life: 3000,
		});

		return;
	}

	if (
		choice === "like" &&
		betslip.activeTab === "multi" &&
		betslip.selection.length === Number(config.public.maximumBetsStrikeMulti) &&
		swipeBetId !== "start"
	) {
		$toast.add({
			severity: "error",
			summary: t("error.title"),
			detail: t("multibet-threshold-reached"),
			life: 3000,
		});
		return;
	}

	swiper.value?.decide(choice);
};

const getCurrentEventId = (): string => {
	if (swipePointers.value.length > 0 && swipePointers.value[0]?.eventId === "start") {
		if (swipedItems.value.length === 0) {
			return "start";
		}
	}

	const currentPointer = swipePointers.value.find((pointer) => pointer.eventId !== "start");
	return currentPointer?.eventId || "start";
};

const getActiveMarketId = (eventId: string, marketIds: string[] | null): string | null => {
	if (!marketIds || marketIds.length === 0) return null;
	const activeIndex = activeMarketIndexes.value.get(eventId) || 0;
	return marketIds[activeIndex] || marketIds[0];
};

const handleChangeMarket = () => {
	if (swipePointers.value.length <= 1) {
		$toast.add({
			severity: "warn",
			summary: t("warning.title"),
			detail: t("no-markets-to-change"),
			life: 3000,
		});
		return;
	}

	const currentPointer = swipePointers.value.find((pointer) => pointer.eventId !== "start");
	if (!currentPointer?.eventId || !currentPointer.marketIds) {
		return;
	}
	const eventId = currentPointer.eventId;
	const availableMarkets = currentPointer.marketIds;

	if (availableMarkets.length <= 1) {
		$toast.add({
			severity: "warn",
			summary: t("warning.title"),
			detail: t("no-other-markets-available"),
			life: 3000,
		});
		return;
	}
	const currentIndex = activeMarketIndexes.value.get(eventId) ?? 0;
	const nextIndex = (currentIndex + 1) % availableMarkets.length;
	activeMarketIndexes.value.set(eventId, nextIndex);

	const newMarketId = availableMarkets[nextIndex];
	if (!newMarketId) return;

	const currentSwipeBet = swipeBets.value.find((bet) => String(bet.selectedEventId) === eventId);
	if (currentSwipeBet) {
		currentSwipeBet.selectedMarketId = newMarketId;

		const marketOutcomes = Array.from(outcomes.value.values()).filter((outcome) => outcome.marketId === newMarketId);

		if (marketOutcomes.length > 0 && marketOutcomes[0]) {
			currentSwipeBet.selectedOutcomeId = marketOutcomes[0].outcomeId;
		}
	}
	const loadedSwipeBet = loadedSwipeBets.value.find((bet) => String(bet.selectedEventId) === eventId);
	if (loadedSwipeBet) {
		loadedSwipeBet.selectedMarketId = newMarketId;
		const marketOutcomes = Array.from(outcomes.value.values()).filter((outcome) => outcome.marketId === newMarketId);

		if (marketOutcomes.length > 0 && marketOutcomes[0]) {
			loadedSwipeBet.selectedOutcomeId = marketOutcomes[0].outcomeId;
		}
	}
};

const handleClickStart = (event: MouseEvent | TouchEvent) => {
	clickStartTime.value = Date.now();

	if (window.TouchEvent && event instanceof TouchEvent) {
		touchStartX.value = event.touches[0].clientX;
		touchStartY.value = event.touches[0].clientY;
	} else if (event instanceof MouseEvent) {
		touchStartX.value = event.clientX;
		touchStartY.value = event.clientY;
	} else {
		return; // fallback in case of unexpected event type
	}
	const rect = swipeCard.value?.getBoundingClientRect();

	if (!rect) return;

	const midpoint = rect.left + rect.width / 2;

	clickSide.value = touchStartX.value < midpoint ? "left" : "right";
};

const handleClickEnd = (event: MouseEvent | TouchEvent, eventId: string) => {
	let touchX = 0;
	let touchY = 0;

	if ("changedTouches" in event) {
		const touch = event.changedTouches[0];
		touchX = touch.clientX;
		touchY = touch.clientY;
	} else {
		touchX = event.clientX;
		touchY = event.clientY;
	}

	const deltaX = Math.abs(touchX - touchStartX.value);
	const deltaY = Math.abs(touchY - touchStartY.value);

	const isSwipe = deltaX > swipeThreshold || deltaY > swipeThreshold;

	if (isSwipe) {
		return;
	}

	const duration = Date.now() - clickStartTime.value;

	if (duration > 300) {
		return;
	}
	const currentPointer = swipePointers.value.find((pointer) => pointer.eventId === eventId);
	if (!currentPointer?.marketIds || currentPointer.marketIds.length <= 1) {
		return;
	}

	const currentIndex = activeMarketIndexes.value.get(eventId) ?? 0;
	let nextIndex: number;

	if (clickSide.value === "left") {
		nextIndex = currentIndex === 0 ? currentPointer.marketIds.length - 1 : currentIndex - 1;
	} else {
		nextIndex = (currentIndex + 1) % currentPointer.marketIds.length;
	}

	activeMarketIndexes.value.set(eventId, nextIndex);

	const newMarketId = currentPointer.marketIds[nextIndex];
	if (!newMarketId) return;

	for (let i = 0; i < swipeBets.value.length; i++) {
		const bet = swipeBets.value[i];
		if (String(bet.selectedEventId) === eventId) {
			bet.selectedMarketId = newMarketId;

			for (const outcome of outcomes.value.values()) {
				if (outcome.marketId === newMarketId) {
					bet.selectedOutcomeId = outcome.outcomeId;
					return;
				}
			}
			break;
		}
	}
};

const handleUndo = () => {
	if (swipedItems.value.length === 0) {
		$toast.add({
			severity: "info",
			detail: t("no-items-to-undo"),
			life: 3000,
		});
		return;
	}

	if (swiper.value?.rewind) {
		const success = swiper.value.rewind();
		if (success) {
			// Decrease swipe count since we're rewinding
			if (swipedCount.value > 0) {
				swipedCount.value--;
			}
		} else {
			$toast.add({
				severity: "info",
				detail: "No more cards to rewind",
				life: 3000,
			});
		}
	}

	const lastSwipedItem = swipedItems.value.shift();
	if (lastSwipedItem) {
		// Remove from betslip if it was a like
		if (lastSwipedItem.decision === "like") {
			// Use the actual outcome ID that was added to betslip, not the original selected one
			const outcomeId = String(lastSwipedItem.actualOutcomeId || lastSwipedItem.selectedOutcomeId);

			// Try to find and remove the selection from betslip
			const selectionIndex = betslip.selection.findIndex((item) => String(item.outcomeId) === outcomeId);
			if (selectionIndex !== -1) {
				betslip.selection.splice(selectionIndex, 1);
			} else {
				// Alternative: Use the betslip's handleSelection method to toggle it off
				// This mimics what happens when you click on an already selected bet
				const event = events.value.get(Number(lastSwipedItem.selectedEventId));
				const market = markets.value.get(lastSwipedItem.selectedMarketId);
				const outcome = outcomes.value.get(outcomeId);
				const price = prices.value.get(outcomeId);

				if (event && market && outcome && price) {
					betslip.handleSelection({
						event: event,
						market: market,
						outcome: outcome,
						price: price,
						score: null,
					});
				}
			}
		}

		// Create base swipe bet without decision property
		const restoredSwipeBet: ISwiperContent = {
			swipeBetId: lastSwipedItem.swipeBetId,
			sportId: lastSwipedItem.sportId,
			selectedEventId: lastSwipedItem.selectedEventId,
			selectedMarketId: lastSwipedItem.selectedMarketId,
			selectedOutcomeId: lastSwipedItem.selectedOutcomeId,
			eventDate: lastSwipedItem.eventDate,
		};

		// Add back to the beginning of swipeBets
		swipeBets.value.unshift(restoredSwipeBet);
		loadedSwipeBets.value.unshift(restoredSwipeBet);
	}
};

watchEffect(() => {
	if (swipePointers.value.length === 0 && !noMoreSwipes.value) {
		offset.value += 10;
	}
});

const shouldShowLoader = computed(() => {
	const isSwipeEmpty = swipePointers.value.filter((p) => p.eventId !== "start").length === 0;
	return (isSwipeEmpty && (loading.value || emopLoading.value || !noMoreSwipes.value)) || isRefetching.value;
});
</script>

<template>
	<SwipeBetHeader :loaded="!loading" :swipe-count="swipedCount" @refetch-swipes="(val) => refetchEmop(val)" :swipe-bets-total="swipeBetsTotal" />
	<LayoutSwipeBetSkeleton v-if="shouldShowLoader" />
	<Swiper
		v-else-if="swipePointers.filter((p: SwipePointer) => p.eventId !== 'start').length > 0 && !isRefetching"
		ref="swiper"
		class="relative h-[340px] md:h-[360px] max-w-[300px] min-w-[290px] m-auto left-0 right-0 top-5 bg-cover z-10 mb-20"
		key-name="eventId"
		v-model="swipePointers"
		:rewind="true"
		:max="3"
		:offset-y="10"
		@submit="onSubmit"
	>
		<template #default="{ data }: { data: SwipePointer }">
			<div
				class="p-1 bg-cover h-[340px] md:h-[360px] max-w-[300px] min-w-[290px] bg-light-200 dark:bg-dark-700 select-none"
				:style="{
					'background-image': `url('${config.public.kenticoDomain}/medialibraries/content.gmgamingsystems.com/Synapse/Misc/swipe-layer.png')`,
				}"
				:class="grabbing ? 'cursor-grabbing' : 'cursor-grab'"
			>
				<LayoutSwipeBetStartCard v-if="data.eventId === 'start'" @swipe="(val) => decide(val, data.eventId)" />
				<div
					v-else
					class="z-50 flex flex-col h-full gap-5 place-items-center"
					@mousedown="handleClickStart"
					@mouseup="(e) => handleClickEnd(e, data.eventId)"
					@touchstart="handleClickStart"
					@touchend="(e) => handleClickEnd(e, data.eventId)"
					ref="swipeCard"
				>
					<div v-if="data.marketIds && data.marketIds.length > 1" class="absolute z-20 flex gap-1 pointer-events-none top-3 left-2 right-2">
						<div
							v-for="(marketId, index) in data.marketIds"
							:key="`market-indicator-${marketId}`"
							class="flex-1 h-1 transition-all transform rounded-full duration-0"
							:class="[
								index === (activeMarketIndexes.get(data.eventId) ?? 0) ? 'bg-white shadow-sm bg-primary-500' : 'bg-light-500 dark:bg-dark-500',
							]"
							:style="{
								boxShadow:
									index === (activeMarketIndexes.get(data.eventId) ?? 0)
										? '0 0 12px rgba(255, 255, 255, 0,6), 0 2px 6px rgba(0, 0, 0, 0.1)'
										: '0 1px 3px rgba(0, 0, 0, 0.1)',
							}"
						></div>
					</div>

					<div class="mt-5 rounded-full">
						<NuxtImg
							:width="38"
							:height="38"
							provider="kentico"
							@error="handleNotFoundImages"
							:src="`/medialibraries/content.gmgamingsystems.com/Synapse/icons/swipe-sports/${
								events.get(Number(data.eventId))?.sportId.replace('---', '-') || 'default'
							}.svg`"
							:draggable="false"
							:alt="events.get(Number(data.eventId))?.sportId || 'sport'"
						/>
					</div>

					<div class="flex flex-col justify-center -mt-3 text-center">
						<p class="flex flex-row items-center justify-center w-full px-1 m-auto text-xs leading-5 text-dark-300 dark:text-light-400">
							<NavIcon
								:width="17"
								:height="17"
								provider="kentico"
								:draggable="false"
								:src="`/icons/regions-leagues-flags/${events.get(Number(data.eventId))?.regionId?.toLowerCase() || 'default'}-circular.svg`"
								:alt="events.get(Number(data.eventId))?.region || 'region'"
								fallback="/icons/regions-leagues-flags/default-circular.svg"
								class="mr-1"
							/>
							<span class="flex-wrap shrink line-clamp-2 text-ellipsis">
								{{ t(events.get(Number(data.eventId))?.region || "region") }}, {{ t(events.get(Number(data.eventId))?.league || "league") }}</span
							>
						</p>

						<p class="flex flex-col w-full px-0 mx-0 text-xs leading-5 text-dark-300 dark:text-light-400">
							<UiDateTime
								class="text-xs"
								dateFormat="dd MMM -"
								timeFormat=" HH:mm"
								time
								:date="Number(events.get(Number(data.eventId))?.expectedStartEpoch)"
							/>
						</p>
					</div>

					<div class="grid w-full grid-cols-8 -mt-3 place-content-around md:mt-1">
						<div class="grid col-span-3 text-center place-items-center">
							<div class="flex items-center justify-center p-2 mb-2 rounded-full bg-light-400 dark:bg-dark-800">
								<NuxtImg
									:width="65"
									:draggable="false"
									:height="65"
									:src="
										getJerserySrc(
											true,
											Number(data.eventId),
											hasLeagueJersey(events.get(Number(data.eventId))?.leagueId, events.get(Number(data.eventId))?.regionId),
											events.get(Number(data.eventId))
										)
									"
									class="object-contain"
									@error="(e: Event) => handleTeamJerseyError(e, events.get(Number(data.eventId))?.homeTeam || '', events.get(Number(data.eventId))?.sportId || '')"
								/>
							</div>
							<p class="mt-1 text-xs font-bold leading-none capitilize">{{ events.get(Number(data.eventId))?.homeTeam || t("home-team") }}</p>
						</div>
						<div class="grid col-span-2 text-6xl leading-10 place-items-center font-italic capitilize text-primary-600">V</div>
						<div class="grid col-span-3 text-center place-items-center">
							<div class="flex items-center justify-center p-2 mb-2 rounded-full bg-light-400 dark:bg-dark-800">
								<NuxtImg
									:width="65"
									:height="65"
									:draggable="false"
									:src="
										getJerserySrc(
											false,
											Number(data.eventId),
											hasLeagueJersey(events.get(Number(data.eventId))?.leagueId, events.get(Number(data.eventId))?.regionId),
											events.get(Number(data.eventId))
										)
									"
									class="object-contain"
									@error="(e: Event) => handleTeamJerseyError(e, events.get(Number(data.eventId))?.awayTeam || '', events.get(Number(data.eventId))?.sportId || '')"
								/>
							</div>
							<p class="mt-1 text-xs font-bold leading-none capitilize">{{ events.get(Number(data.eventId))?.awayTeam || t("away-team") }}</p>
						</div>
					</div>

					<div v-if="data.marketIds && data.marketIds.length" class="flex items-center justify-center mb-8 h-4/5">
						<div
							v-if="data.marketIds && markets.get(data.marketIds[activeMarketIndexes.get(data.eventId) ?? 0])"
							class="flex flex-col items-center justify-center font-bold text-center text-md"
						>
							<div class="flex items-center px-1">
								<UiIcon class="w-3 h-3 mr-1 fill-primary-500" :path="mdiArrowLeft" />
								<div class="w-5/6 text-xs leading-4 text-primary-600">
									{{ markets.get(data.marketIds[activeMarketIndexes.get(data.eventId) ?? 0])?.displayName }}
								</div>
								<UiIcon class="w-3 h-3 ml-1 fill-primary-500" :path="mdiArrowRight" />
							</div>
							<div
								v-for="outcome in Array.from(outcomes.values())
									.filter((o) => {
										const activeMarketId = data.marketIds ? data.marketIds[activeMarketIndexes.get(data.eventId) ?? 0] : null;
										return activeMarketId && (String(o.marketId) === String(activeMarketId) || String(o.marketId) === `_${activeMarketId}`);
									})
									.filter((o) => o.displayName && o.displayName.trim() !== '')
									.slice(0, 1)"
								:key="outcome.outcomeId"
							>
								<p class="text-sm leading-7">
									{{ outcome.displayName }}
									<span v-if="outcome.sbv" class="text-xs">
										{{ outcome.sbv }}
									</span>
								</p>
							</div>
						</div>
					</div>

					<div class="absolute bottom-2">
						<div class="flex flex-row items-start justify-center flex-grow" v-if="data.marketIds && data.marketIds.length">
							<template v-if="data.marketIds && markets.get(data.marketIds[activeMarketIndexes.get(data.eventId) ?? 0])">
								<UiIcon
									v-if="markets.get(data.marketIds[activeMarketIndexes.get(data.eventId) ?? 0])?.isBoosted"
									:path="betwayBoostedOdds"
									class="w-4 h-4 fill-warning-600"
								/>
								<p class="flex content-center mx-1 text-xs">
									{{ t("odds") }}:
									<template
										v-for="outcome in Array.from(outcomes.values())
											.filter((o) => {
												const activeMarketId = data.marketIds ? data.marketIds[activeMarketIndexes.get(data.eventId) ?? 0] : null;
												return activeMarketId && (String(o.marketId) === String(activeMarketId) || String(o.marketId) === `_${activeMarketId}`);
											})
											.filter((o) => o.displayName && o.displayName.trim() !== '')
											.slice(0, 1)"
										:key="outcome.outcomeId"
									>
										<span class="ml-0.5 font-bold" v-if="prices.get(outcome.outcomeId)">
											{{
												formatOdds(
													Number(prices.get(outcome.outcomeId)?.priceDecimal),
													Number(prices.get(outcome.outcomeId)?.numerator),
													Number(prices.get(outcome.outcomeId)?.denominator)
												)
											}}
										</span>
									</template>
								</p>
								<UiIcon
									v-if="markets.get(data.marketIds[activeMarketIndexes.get(data.eventId) ?? 0])?.isCashOutAllowed"
									:path="betwayBetbookCashout"
									class="w-4 h-4 fill-primary-300 mt-0.5"
									viewBox="0 0 16 16"
								/>
							</template>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template #like>
			<UiIcon class="left-2.5 absolute m-auto like-pointer mr-2 fill-primary-300 opacity-40" :path="mdiCheck" style="top: 5px" />
		</template>
		<template #nope>
			<UiIcon
				class="right-2.5 top-2 absolute m-auto nope-pointer mr-2 fill-dark-400 dark:fill-light-900 opacity-40"
				:path="mdiClose"
				style="top: 5px"
			/>
		</template>
	</Swiper>

	<div class="flex items-center justify-center gap-5 -mt-6" v-if="swipePointers.length">
		<div class="flex flex-col items-center gap-1">
			<div
				@click="handleUndoClick"
				:class="[
					'flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 group',
					undoDisabled
						? 'cursor-not-allowed opacity-50 bg-light-200 dark:bg-dark-800'
						: 'cursor-pointer  hover:shadow-lg  dark:hover:bg-dark-600 hover:bg-primary-50  dark:hover:bg-primary-600/40 dark:bg-dark-700 bg-light-50',
				]"
			>
				<UiIcon
					:class="[
						'w-4 h-4 transition-all duration-200',
						undoDisabled ? 'fill-dark-300 dark:fill-dark-500' : 'dark:fill-light-50 group-hover:scale-110 ',
					]"
					:path="mdiArrowULeftTop"
				/>
			</div>
			<p :class="['text-xs text-center select-none', undoDisabled ? 'text-dark-300' : 'text-dark-100']">{{ t("undo") }}</p>
		</div>

		<div class="flex flex-col items-center gap-1 m-0.5">
			<div
				@click="handlePassClick"
				:class="[
					'flex items-center justify-center w-12 h-12 shadow-xl rounded-xl transition-all duration-200 group',
					buttonsDisabled
						? 'cursor-not-allowed opacity-50 bg-light-200 dark:bg-dark-800'
						: 'cursor-pointer hover:shadow-2xl dark:hover:bg-dark-600 hover:bg-primary-50  dark:hover:bg-primary-600/40 dark:bg-dark-700 bg-light-50',
				]"
			>
				<UiIcon
					:class="[
						'w-5 h-5 transition-all duration-200',
						buttonsDisabled ? 'fill-dark-300 dark:fill-dark-500' : 'dark:fill-light-50 group-hover:fill-red-500 group-hover:scale-110 ',
					]"
					:path="mdiClose"
				/>
			</div>
			<p :class="['text-xs text-center select-none', buttonsDisabled ? 'text-dark-300' : 'text-dark-100']">{{ t("pass") }}</p>
		</div>

		<div class="flex flex-col items-center gap-1">
			<div
				@click="handleAddClick"
				:class="[
					'flex items-center justify-center w-12 h-12 shadow-xl rounded-xl transition-all duration-200 group',
					buttonsDisabled
						? 'cursor-not-allowed opacity-50 bg-light-200 dark:bg-dark-800'
						: 'cursor-pointer hover:shadow-2xl hover:bg-primary-50  dark:hover:bg-primary-600/40 dark:bg-dark-700 bg-light-50',
				]"
			>
				<UiIcon
					:class="[
						'w-5 h-5 transition-all duration-200',
						buttonsDisabled ? 'fill-dark-300 dark:fill-dark-500' : 'fill-primary-500 group-hover:fill-primary-600 group-hover:scale-110 ',
					]"
					:path="mdiCheckboxMarkedCirclePlusOutline"
				/>
			</div>

			<div class="flex flex-row items-center justify-center text-xs text-center select-none text-dark-100">
				{{ t("add-number") }} <UiIcon class="w-3 h-3 ml-1 fill-dark-50" :path="mdiArrowRight" />
			</div>
		</div>

		<div class="flex flex-col items-center gap-1">
			<div
				@click="handleChangeMarketClick"
				:class="[
					'flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 group',
					changeMarketDisabled
						? 'cursor-not-allowed opacity-50 bg-light-200 dark:bg-dark-800'
						: 'cursor-pointer  hover:shadow-lg  hover:bg-primary-50 dark:hover:bg-primary-600/40 dark:hover:bg-dark-600 dark:bg-dark-700 bg-light-50',
				]"
			>
				<UiIcon
					:class="[
						'w-4 h-4 transition-all duration-200',
						changeMarketDisabled ? 'fill-dark-300 dark:fill-dark-500' : 'dark:fill-light-50 group-hover:scale-110 ',
					]"
					:path="mdiUnfoldMoreHorizontal"
				/>
			</div>
			<p :class="['w-12 text-xs leading-3 text-center select-none', changeMarketDisabled ? 'text-dark-300' : 'text-dark-100']">
				{{ t("change-market") }}
			</p>
		</div>
	</div>

	<SwipeBetTutorial />

	<SwipeBetNoMoreSwipes
		v-if="noMoreSwipes && !loading && !isRefetching && !emopLoading && swipePointers.filter((p: SwipePointer) => p.eventId !== 'start').length === 0"
		class="z-0"
	/>
</template>
